import { Box, Button, Divider, Link, Paper, Stack, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import moment from 'moment';
import React, { ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { FileStructureContext } from '../../../contexts/FileStructureContext';
import { GroupSettingsContext } from '../../../contexts/GroupSettingsContext';
import { TableContext } from '../../../contexts/TableContext';
import useBackwardsCompatible from '../../../hooks/useBackwardsCompatible';
import useCompaniesTableColumns from '../../../hooks/useCompaniesTableColumns';
import { CompanyFile, Dashboard, EmailBody } from '../../../types/files';
import ArrayUtils from '../../../utils/ArrayUtils';
import FileAttachment from '../../atoms/dashboard-details-v2/FileAttachment';
import MultiSelector from '../../atoms/MultiSelector';
import EmailsModal from '../../modals/tables/dashboards/rows/OldEmailsModal-X';
import CompanyMiniDeckViewer from '../../molecules/dashboard-deck/CompanyMiniDeckViewer';
import { ActivityTimeline } from '../../molecules/weeklyPipeline/ActivityTimeline';

const edjsParser = require("editorjs-parser").default;
const parser = new edjsParser();

type TimelineCardProps = {
	dashboard: Dashboard|null;
};

const useStyles = makeStyles((theme) => ({
    paper: {
        borderRadius: theme.spacing(1),
    },
    header: {
        padding: theme.spacing(3),
        borderBottom: '1px solid #f1f3f5',
    },
    headerTitle: {
        fontSize: 16,
        fontWeight: 600,
        color: '#2c3e50',
        marginBottom: theme.spacing(2),
    },
    filterLabel: {
        fontSize: 13,
        color: '#6c757d',
        fontWeight: 500,
    },
    filterButton: {
        textTransform: 'capitalize',
        fontSize: 13,
    },
    filterButtonActive: {
        backgroundColor: '#1a8c8c',
        '&:hover': {
            backgroundColor: '#167676',
        },
    },
    bodyWrapper: {
        display: 'flex',
    },
    bodyLeft: {
        flex: 1,
        minWidth: 0,
    },
    bodyRight: {
        width: 340,
        flexShrink: 0,
        padding: theme.spacing(3),
        paddingLeft: 0,
    },
    content: {
        padding: theme.spacing(3),
    },
    dateColumn: {
        width: 120,
        textAlign: 'right',
        paddingRight: theme.spacing(3),
        borderRight: '2px solid #e9ecef',
        position: 'relative',
        '&::after': {
            content: '""',
            position: 'absolute',
            right: -7,
            top: 4,
            width: 12,
            height: 12,
            backgroundColor: '#1a8c8c',
            borderRadius: '50%',
        },
    },
    dateText: {
        fontSize: 13,
        color: '#6c757d',
        fontWeight: 500,
    },
    contentColumn: {
        flex: 1,
        paddingLeft: theme.spacing(1),
    },
    updateType: {
        fontSize: 14,
        fontWeight: 400,
        color: '#6c757d',
        marginBottom: theme.spacing(1),
		position: 'relative',
		paddingLeft: '12px',
		"&::before": {
		  content: '""',
		  width: '8px',
		  height: '8px',
		  borderRadius: '50%',
		  background: '#f59e0b',
		  position: 'absolute',
		  left: 0,
		  top: '50%',
		  transform: 'translateY(-50%)'
		}
    },
    newType: {
        fontSize: 14,
        fontWeight: 400,
        color: '#6c757d',
        marginBottom: theme.spacing(1),
		position: 'relative',
		paddingLeft: '12px',
		"&::before": {
		  content: '""',
		  width: '8px',
		  height: '8px',
		  borderRadius: '50%',
		  background: '#408bdf',
		  position: 'absolute',
		  left: 0,
		  top: '50%',
		  transform: 'translateY(-50%)'
		}
    },
    subject: {
        fontSize: 15,
        fontWeight: 600,
        color: '#2c3e50',
        marginBottom: theme.spacing(2),
    },
    dataFieldBox: {
        border: '1px solid #e9ecef',
        padding: theme.spacing(2),
        backgroundColor: '#fafbfc',
        borderRadius: theme.spacing(1),
    },
    fieldLabel: {
        fontSize: 12,
        color: '#6c757d',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        marginBottom: theme.spacing(1),
    },
    fieldText: {
        fontSize: 14,
        color: '#2c3e50',
        lineHeight: 1.6,
    },
    emailMeta: {
        fontSize: 13,
        color: '#adb5bd',
        marginTop: theme.spacing(1.5),
    },
    viewEmailLink: {
        marginTop: theme.spacing(1.5),
        display: 'inline-block',
        color: '#1a8c8c',
        textDecoration: 'none',
        fontSize: 13,
        fontWeight: 500,
        '&:hover': {
            textDecoration: 'underline',
            color: '#167676',
        },
    },
}));

const TimelineCard: React.FC<TimelineCardProps> = ({ dashboard }) => {
    const classes = useStyles();
	const { tagGroups } = useContext(GroupSettingsContext);
	const { fileStructure } = useContext(FileStructureContext);
    const { headers } = useContext(TableContext);
    const { defaultColumns } = useCompaniesTableColumns();
    const { dashboardOwner } = useBackwardsCompatible();

    const [comModalOpen, setComModalOpen] = useState<boolean>(false);
	const [deckViewerModalOpen, setDeckViewerModalOpen] = useState<boolean>(false);
	const [selectedFile, setSelectedFile] = useState<CompanyFile|undefined>(undefined);
    const [emailActivity, setEmailActivity] = useState<EmailBody>({} as EmailBody);
    const [filter, setFilter] = useState<string>('All');

    const owner = useMemo(() => dashboardOwner(dashboard!), [dashboard, dashboardOwner]);

	const files = useMemo(() => fileStructure
        .filter(file => !file.isDirectory && file.dashboardId === dashboard?.id)
        .sort((dA, dB) => moment(dB.createdAt).diff(moment(dA.createdAt)))
    , [dashboard, fileStructure]);

	const [createEmail, ...otherEmails]  = useMemo(() => {
        const activityItems: EmailBody[] = dashboard?.emailBodies
            ?.filter(emailBody => ['EMAIL', 'FULL_EMAIL'].includes(emailBody?.type || '')) as EmailBody[];
        const emailActivities: EmailBody[] =  ArrayUtils.sortByAscending(activityItems ?? [], 'createdAt');
        const unmatchedFiles: CompanyFile[] = [];
        const fileMatchedMap = new Map<string, boolean>();
        const unmatchedLinks: string[] = [];
        const linkMatchedMap = new Map<string, boolean>();

        const emailsWithDocs: Array<EmailBody & { attached: CompanyFile[], linked: string[] }> = emailActivities.map(emailBody => ({ ...emailBody,
            attached: (() => {
                const matched = new Map<string, CompanyFile>();
                const attachments = emailBody.attachments?.map(fileName => fileName.trim().toLowerCase()) || [];

                files.forEach(file => {
                    const timeDiff = moment(file.createdAt).diff(moment(emailBody.createdAt), 'minutes', true);
                    if (attachments.includes(file.name.trim().toLowerCase()) || (timeDiff >= 0 && timeDiff <= 5)) {
                        if (!matched.has(file.id)) {
                            matched.set(file.id, file);
                            fileMatchedMap.set(file.id, true);
                        }
                    }
                });

                return Array.from(matched.values());
            })(),
            linked: (() => {
                const seen = new Set<string>();
                const matchedLinks: string[] = [];
                const linksFromEmail = emailBody.externalLinks?.map(extLink => extLink.trim()) || [];

                (dashboard?.externalLinks || []).forEach(link => {
                    const t = link.trim();
                    if ((linksFromEmail.includes(t) || (emailBody?.content || '').includes(t)) && !seen.has(t)) {
                        seen.add(t);
                        matchedLinks.push(t);
                        linkMatchedMap.set(t, true);
                    }
                });

                return matchedLinks;
            })(),
        }));

        files.forEach(file => !fileMatchedMap.has(file.id) ? unmatchedFiles.push(file) : {});
        dashboard?.externalLinks?.forEach(link => !linkMatchedMap.has(link.trim()) ? unmatchedLinks.push(link) : {});
        if (emailsWithDocs.length > 0) {
            if (unmatchedFiles.length > 0)
                emailsWithDocs[0].attached = [ ...emailsWithDocs[0].attached, ...unmatchedFiles];
            if (unmatchedLinks.length > 0)
                emailsWithDocs[0].linked = [ ...emailsWithDocs[0].linked, ...unmatchedLinks];
        } else if (unmatchedFiles.length > 0 || unmatchedLinks.length > 0) {
            // Create a synthetic email entry for unmatched files/links when no emails exist
            emailsWithDocs.push({
                id: `${dashboard?.id}-unmatched`,
                createdAt: unmatchedFiles[0]?.createdAt || dashboard?.createdAt,
                type: 'EMAIL',
                attached: unmatchedFiles,
                linked: unmatchedLinks,
            } as EmailBody & { attached: CompanyFile[], linked: string[] });
        }

        return emailsWithDocs;
    // eslint-disable-next-line
    }, [dashboard, files]);

    const commentaryNote = useMemo(() => {
        const commentary = dashboard?.notes?.find(note => note.title === `$['Commentary']`);
		return new DOMParser().parseFromString(parser.parse({ blocks: (JSON.parse(commentary?.data || '[]') as any[]).slice(1) }), 'text/html')?.body?.textContent?.trim() || '';
    }, [dashboard?.notes]);

	const lookUpTagsSelection = useCallback((tag: string) =>
		dashboard?.tagGroups?.find(({ title }) => title.toLowerCase() === tag.toLowerCase())?.tags || []
	, [dashboard]);

    const filterOptions = useMemo(() => {
        const columns = headers ?? defaultColumns.map(col => col.id) as string[];
        const tagGroupsOptions = tagGroups.filter(({ title }) => title !== 'Sectors' && !!lookUpTagsSelection(title)?.length).map(({ title }) => title);
        const attachmentOptions = (!!files?.length || !!dashboard?.externalLinks?.length) ? ['Documents'] : [];

        return ['All', ...attachmentOptions, ...(columns || [])?.filter(column => (!!commentaryNote && ['Commentary'].includes(column))
            || tagGroupsOptions.includes(column)
        )] as string[];
    // eslint-disable-next-line
    }, [dashboard, files, tagGroups, commentaryNote, headers, defaultColumns]);

    const RenderFields: {[key: string]: ReactNode} = useMemo(() => ({
        'Commentary': !!commentaryNote && (filter === 'All' || filter === 'Commentary') ? (
            <Box className={classes.dataFieldBox}>
                <Typography className={classes.fieldLabel}>{'Commentary'}</Typography>
                <Typography className={classes.fieldText}>
                    {commentaryNote}
                </Typography>
            </Box>
        ) : null,
        ...Object.fromEntries(tagGroups.filter(({ title }) => title !== 'Sectors')
            .map(({ title, tags }, i) => !!lookUpTagsSelection(title)?.length
                && (filter === 'All' || filter === title) ? [title, (
                    <Box className={classes.dataFieldBox} key={'dashboard-details-page-860-' + i}>
                        <Typography className={classes.fieldLabel}>{title}</Typography>
                        <Box sx={{ transform: 'scale(1.1) transformX(-10px)' }}>
                            <MultiSelector
                                mainOptions={tags}
                                selections={lookUpTagsSelection(title)}
                                fullSpan readOnly/>
                        </Box>
                    </Box>
        )] : [title, null])),
    // eslint-disable-next-line
    }), [dashboard, commentaryNote, files, tagGroups, filter]);

    return (<>
        <Paper elevation={0} className={classes.paper}>
            <Box className={classes.bodyWrapper}>
            <Box className={classes.bodyLeft}>
            <Box className={classes.header}>
                <Typography className={classes.headerTitle}>Email Timeline</Typography>
                <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                    <Typography className={classes.filterLabel}>Show:</Typography>
                    {filterOptions.map((f) => (
                        <Button key={f} size="small"
                            variant={filter === f ? 'contained' : 'outlined'}
                            className={`${classes.filterButton}
								${filter === f ? classes.filterButtonActive : ''}
							`} onClick={() => setFilter(f)}>
                            {f === 'all' ? 'All Fields' : f}
                        </Button>
                    ))}
                </Stack>
            </Box>
            <Box className={classes.content}>
                <Stack spacing={3} divider={<Divider />}>
                    {otherEmails?.reverse()?.map((email, index) => (
                        <Stack key={index} direction="row" spacing={3}>
                            <Box className={classes.dateColumn}>
                                <Typography className={classes.dateText}>{moment(email.createdAt).format("MMM D, YYYY")}</Typography>
                            </Box>
                            <Box className={classes.contentColumn}>
                                {email.contentClassification === 'COMPANY_UPDATE' ? (
                                    <Typography className={classes.updateType}>
                                        {'Company Update'}
                                    </Typography>
                                ) : (
                                    <Typography className={classes.newType}>
                                        {'New deal'}
                                    </Typography>
                                )}
                                <Typography className={classes.subject}>{email.subject}</Typography>
								<Stack spacing={2}>
                                    {(!!email?.attached?.length || !!email?.linked?.length) && (filter === 'All' || filter === 'Documents') && (
                                        <Box className={classes.dataFieldBox}>
                                            <Typography className={classes.fieldLabel}>
                                                {`Documents (${(email?.attached?.length || 0) + (email?.linked?.length || 0)})`}
                                            </Typography>
                                            <Stack direction="row" flexWrap="wrap" gap={1}>
                                                {email?.attached?.map((file, i) => (
                                                    <Box onClick={() => {
                                                        setSelectedFile(file);
                                                        setDeckViewerModalOpen(true);
                                                    }} key={'dashboard-details-page-909-' + i}>
                                                        <FileAttachment resName={file.name} file={file} />
                                                    </Box>
                                                ))}
                                                {email?.linked?.map((link, i, links) => (
                                                    <Box key={'dashboard-details-page-924-' + i}>
                                                        <FileAttachment resName={link}
                                                            file={files?.length === 1 && links?.length === 1 ? files[0] : undefined} />
                                                    </Box>
                                                ))}
                                            </Stack>
                                        </Box>
                                    )}
								</Stack>
                                <Typography className={classes.emailMeta}>
                                    {`From: ${email?.from?.replace(/<.*?>/g, '')}`}
                                </Typography>
                                <Link href="#" className={classes.viewEmailLink}
									onClick={() => {setEmailActivity(email); setComModalOpen(true);}}>
                                    {'View full email →'}
                                </Link>
                            </Box>
                        </Stack>
                    ))}
					{createEmail ? (
						<Stack direction="row" spacing={3}>
							<Box className={classes.dateColumn}>
								<Typography className={classes.dateText}>
									{moment(dashboard?.createdAt).format("MMM D, YYYY")}
								</Typography>
							</Box>
							<Box className={classes.contentColumn}>
                                {createEmail.contentClassification === 'COMPANY_UPDATE' ? (
                                    <Typography className={classes.updateType}>
                                        {'Company Update'}
                                    </Typography>
                                ) : (
                                    <Typography className={classes.newType}>
                                        {'New deal'}
                                    </Typography>
                                )}
								<Typography className={classes.subject}>{createEmail.subject}</Typography>
								<Stack spacing={2}>
                                    {(!!createEmail?.attached?.length || !!createEmail?.linked?.length) && (filter === 'All' || filter === 'Documents') && (
                                        <Box className={classes.dataFieldBox}>
                                            <Typography className={classes.fieldLabel}>
                                                {`Documents (${(createEmail?.attached?.length || 0) + (createEmail?.linked?.length || 0)})`}
                                            </Typography>
                                            <Stack direction="row" flexWrap="wrap" gap={1}>
                                                {createEmail?.attached?.map((file, i) => (
                                                    <Box onClick={() => {
                                                        setSelectedFile(file);
                                                        setDeckViewerModalOpen(true);
                                                    }} key={'dashboard-details-page-909-' + i}>
                                                        <FileAttachment resName={file.name} file={file} />
                                                    </Box>
                                                ))}
                                                {createEmail?.linked?.map((link, i, links) => (
                                                    <Box key={'dashboard-details-page-924-' + i}>
                                                        <FileAttachment resName={link}
                                                            file={files?.length === 1 && links?.length === 1 ? files[0] : undefined} />
                                                    </Box>
                                                ))}
                                            </Stack>
                                        </Box>
                                    )}
                                    {filterOptions?.map(field => RenderFields[field])}
								</Stack>
								<Typography className={classes.emailMeta}>
									{`From: ${createEmail?.from?.replace(/<.*?>/g, '')}`}
								</Typography>
								<Link href="#" className={classes.viewEmailLink}
									onClick={() => {setEmailActivity(createEmail); setComModalOpen(true);}}>
									{`View full email →`}
								</Link>
							</Box>
						</Stack>
					) : !!files?.length && (
						<Stack direction="row" spacing={3}>
							<Box className={classes.dateColumn}>
								<Typography className={classes.dateText}>
									{moment(dashboard?.createdAt).format("MMM D, YYYY")}
								</Typography>
							</Box>
							<Box className={classes.contentColumn}>
                                <Typography className={classes.newType}>
                                    {'New deal'}
                                </Typography>
								<Typography className={classes.subject}>{'File uploaded'}</Typography>
								<Stack spacing={2}>
                                    {filterOptions?.map(field => RenderFields[field])}
								</Stack>
								<Typography className={classes.emailMeta}>
									{`Added by: ${owner?.name}`}
								</Typography>
							</Box>
						</Stack>
					)}
                    {!otherEmails.length && !createEmail && !files?.length && (
                        <Stack alignItems="center" justifyContent="center" height="50vh" pb={4}>
                            <Typography fontFamily="Lato" fontWeight="600" color="#2c3e50">
                                {'No email or files yet.'}
                            </Typography>
                        </Stack>
                    )}
                </Stack>
            </Box>
            </Box>
            <Box className={classes.bodyRight}>
                <ActivityTimeline dashboard={dashboard!} />
            </Box>
            </Box>
        </Paper>
        {deckViewerModalOpen && (
            <CompanyMiniDeckViewer
                open={deckViewerModalOpen}
                defaultDeck={selectedFile}
                externalDeck={dashboard?.externalLinks?.at(-1)}
                onClose={() => setDeckViewerModalOpen(false) } />
        )}
        {comModalOpen && (
            <EmailsModal open={comModalOpen}
                emailActivities={[emailActivity]}
                dashboard={dashboard!}
                onClose={() => setComModalOpen(false)} />
        )}
    </>);
};

export default TimelineCard;